import React, { useEffect, useState } from 'react'
import { Badge, Label, Select, Spinner } from 'flowbite-react'
import { HiChevronDown, HiChevronRight, HiStar } from 'react-icons/hi'
import { toastApiError } from '../utils/apiError'
import { listAuthorityTypes, listDefaultDepartments } from '../utils/departments'

/**
 * Read-only browser for the default department/AoR template sets, keyed by
 * authority type. There is no server-side edit or reset endpoint — this exists
 * purely for reference, e.g. so staff who deleted a template-derived row can see
 * what it looked like and recreate it by hand.
 */
const DefaultTemplatesPanel = ({ token, defaultTypeId = null }) => {
  const [open, setOpen] = useState(false)
  const [types, setTypes] = useState([])
  const [typesLoading, setTypesLoading] = useState(false)
  const [selectedTypeId, setSelectedTypeId] = useState('')
  const [defaults, setDefaults] = useState([])
  const [defaultsLoading, setDefaultsLoading] = useState(false)

  useEffect(() => {
    if (!open || types.length > 0) return
    let cancelled = false
    const run = async () => {
      setTypesLoading(true)
      try {
        const data = await listAuthorityTypes(token)
        if (cancelled) return
        const list = Array.isArray(data) ? data : []
        setTypes(list)
        const preferred =
          defaultTypeId != null && list.some((type) => type.id === Number(defaultTypeId))
            ? String(defaultTypeId)
            : list.length > 0
              ? String(list[0].id)
              : ''
        setSelectedTypeId(preferred)
      } catch (error) {
        toastApiError(error, 'Failed to load authority types')
      } finally {
        if (!cancelled) setTypesLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [open, token, defaultTypeId, types.length])

  useEffect(() => {
    if (!selectedTypeId) {
      setDefaults([])
      return
    }
    let cancelled = false
    const run = async () => {
      setDefaultsLoading(true)
      try {
        const data = await listDefaultDepartments(token, selectedTypeId)
        if (!cancelled) setDefaults(Array.isArray(data) ? data : [])
      } catch (error) {
        if (!cancelled) toastApiError(error, 'Failed to load default templates')
      } finally {
        if (!cancelled) setDefaultsLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [token, selectedTypeId])

  return (
    <div className="rounded-lg bg-white shadow dark:bg-gray-800">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Default templates
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Reference-only. Browse the default department set each authority type is cloned from.
          </p>
        </div>
        {open ? (
          <HiChevronDown className="h-5 w-5 shrink-0 text-gray-400" />
        ) : (
          <HiChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
        )}
      </button>

      {open && (
        <div className="space-y-4 border-t border-gray-100 px-6 py-4 dark:border-gray-700">
          {typesLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Spinner size="sm" /> Loading authority types…
            </div>
          ) : (
            <div className="max-w-sm">
              <Label htmlFor="template-type" className="mb-1 block">
                Authority type
              </Label>
              <Select
                id="template-type"
                value={selectedTypeId}
                onChange={(event) => setSelectedTypeId(event.target.value)}
              >
                <option value="">Select an authority type</option>
                {types.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                    {defaultTypeId != null && type.id === Number(defaultTypeId)
                      ? ' (this authority)'
                      : ''}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {defaultsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Spinner size="sm" /> Loading templates…
            </div>
          ) : selectedTypeId && defaults.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No default departments defined for this authority type.
            </p>
          ) : (
            <ul className="space-y-3">
              {defaults.map((department) => (
                <li
                  key={department.id}
                  className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {department.name}
                    </span>
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                      {department.slug}
                    </span>
                    {department.is_catch_all && (
                      <Badge color="indigo" icon={HiStar}>
                        Catch-all
                      </Badge>
                    )}
                  </div>
                  {department.description && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {department.description}
                    </p>
                  )}
                  {department.areas_of_responsibility?.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {department.areas_of_responsibility.map((aor) => (
                        <li key={aor.id}>
                          <Badge color={aor.is_catch_all ? 'indigo' : 'gray'}>
                            {aor.name}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default DefaultTemplatesPanel
